import React, { createContext, useContext, useState } from 'react';
import api, { getApiBaseUrl } from '../utils/axios'; // ✅ Correct
import axios from 'axios';


// Create the context
const ApiContext = createContext();

// ApiProvider component
export const ApiProvider = ({ children }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Base URL for API requests
const API_BASE_URL = import.meta.env.MODE === 'development'
  ? 'http://localhost:5000/api'
  : 'https://notedin.onrender.com/api';

  // Helper function for headers (in case auth is needed)
  const getHeaders = () => {
    return {
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `Bearer ${token}`, // Uncomment if you use auth tokens
      },
    };
  };

  // API methods

  // Resolve Invitation
  const resolveInvitation = async (invitationId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/invitation/resolve/${invitationId}`,
        getHeaders()
      );
      console.log('Resolved Invitation Data:', response.data);
      return response.data;
    } catch (err) {
      console.error('Error resolving invitation:', err);
      throw err;
    }
  };

  // Fetch assessments by checkpoint ID
const getAssessmentsByCheckpoint = async (checkpointId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/assessments_by_checkpoint/get_assessments?checkpoint_id=${checkpointId}`,
      getHeaders()
    );
    console.log('Assessments by Checkpoint:', response.data);
    return response.data;
  } catch (err) {
    console.error('Error fetching assessments by checkpoint:', err);
    throw err;
  }
};

  // Get Assessment by ID
  const getAssessment = async (assessmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/assessment/assessment_requests/${assessmentId}`,
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching assessment:', err);
      throw err;
    }
  };

  // Update Assessment by ID
  const updateAssessment = async (assessmentId, updateData) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/assessment/assessment_requests/${assessmentId}`,
        updateData,
        getHeaders()
      );
      console.log('Assessment updated:', response.data);
      return response.data;
    } catch (err) {
      console.error('Error updating assessment:', err);
      throw err;
    }
  };

  // Invite to Assessment
  const inviteToAssessment = async (assessmentId, checkpointId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/assessment_requests/assessment/invite`,
        { assessmentId, checkpointId },
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error inviting to assessment:', err);
      throw err;
    }
  };

  // Accept Invitation API function
  const acceptInvitation = async (assessmentId, invitationId, userId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/invitation/assessment_requests/${assessmentId}/accept/${invitationId}`,
        { user_id: userId },
        getHeaders()
      );
      console.log('Invitation accepted:', response.data);
      return response.data;
    } catch (err) {
      console.error('Error accepting invitation:', err);
      throw err;
    }
  };

  // Reject Invitation API function
  const rejectInvitation = async (assessmentId, invitationId, userId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/invitation/assessment_requests/${assessmentId}/reject/${invitationId}`,
        { user_id: userId },
        getHeaders()
      );
      console.log('Invitation rejected:', response.data);
      return response.data;
    } catch (err) {
      console.error('Error rejecting invitation:', err);
      throw err;
    }
  };

  // Update Invitation Status
  const updateInvitationStatus = async (assessmentId, invitationId, status) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/assessment_requests/invitation/status`,
        { assessmentId, invitationId, status },
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error updating invitation status:', err);
      throw err;
    }
  };

  // Delete Assessment by ID
  const deleteAssessment = async (assessmentId) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/assessment/${assessmentId}`,
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error deleting assessment:', err);
      throw err;
    }
  };

  // Start Inspection
  const startInspection = async (assessmentId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/start-inspection/${assessmentId}`,
        {},
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error starting inspection:', err);
      throw err;
    }
  };

  // Add Inspection Notes
  const addInspectionNotes = async (assessmentId, notes) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/add-inspection-notes/${assessmentId}`,
        { notes },
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error adding inspection notes:', err);
      throw err;
    }
  };

  // View Inspection Notes
  const viewInspectionNotes = async (assessmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/view-inspection-notes/${assessmentId}`,
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching inspection notes:', err);
      throw err;
    }
  };

  // Submit Final Report
  const submitFinalReport = async (assessmentId, reportDetails) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/submit-final-report/${assessmentId}`,
        { report_details: reportDetails },
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error submitting final report:', err);
      throw err;
    }
  };

  // Get Assessment Report
  const getAssessmentReport = async (assessmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/get-report/${assessmentId}`,
        getHeaders()
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching assessment report:', err);
      throw err;
    }
  };

  return (
    <ApiContext.Provider
      value={{
        resolveInvitation,
        getAssessment,
        updateAssessment,
        inviteToAssessment,
        acceptInvitation,
        rejectInvitation,
        updateInvitationStatus,
        deleteAssessment,
        startInspection,
        addInspectionNotes,
        viewInspectionNotes,
        submitFinalReport,
        getAssessmentReport,
        getAssessmentsByCheckpoint, // Add this here
        error,
        loading,
        setError,
        setLoading,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

// Custom hook to use the ApiContext
export const useApi = () => {
  return useContext(ApiContext);
};
